import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { t } from "@/lib/i18n";
import "@/lib/i18n_p1";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle } from "lucide-react";

export default function InviteAccept() {
  const { token } = useParams();
  const { user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    api.get(`/workspaces/invites/lookup/${token}`)
      .then(({ data }) => setInvite(data))
      .catch((e) => setError(e.response?.data?.detail || "Invite not found"));
  }, [token]);

  const accept = async () => {
    if (!user) {
      try {
        localStorage.setItem("lal_pending_invite", token);
      } catch { /* ignore */ }
      navigate(`/login?invite=${token}`);
      return;
    }
    setAccepting(true);
    try {
      await api.post(`/workspaces/invites/accept/${token}`);
      toast.success("Invite accepted");
      navigate("/team");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
      setAccepting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col">
      <header className="border-b border-[#E9ECEF] bg-white">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 md:px-8 h-16">
          <span className="font-display text-xl flex items-center gap-3">
            <span className="inline-block w-2 h-6 bg-[#C65D47]" />
            Law Pilot
          </span>
          <LanguageSwitcher />
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border border-[#E9ECEF] p-10 text-center" data-testid="invite-accept-card">
          {error ? (
            <>
              <AlertTriangle className="w-12 h-12 text-[#C65D47] mx-auto" />
              <h1 className="mt-6 font-display text-2xl">{error}</h1>
            </>
          ) : !invite ? (
            <div className="text-[#868E96]">{t(lang,"common.loading")}</div>
          ) : (
            <>
              <CheckCircle2 className="w-12 h-12 text-[#C65D47] mx-auto" />
              <h1 className="mt-6 font-display text-2xl">{t(lang,"team.join_title")}</h1>
              <p className="mt-2 text-[#495057]">{t(lang,"team.join_sub")} <strong>{invite.workspace?.name}</strong></p>
              <p className="mt-1 text-xs text-[#868E96]">Invited as: {invite.invite?.email} ({invite.invite?.role})</p>
              {!user && <p className="mt-4 text-sm text-[#C65D47]">{t(lang,"team.join_need_login")}</p>}
              <button
                onClick={accept}
                disabled={accepting}
                className="mt-8 inline-flex items-center gap-2 bg-[#0A1128] text-white px-6 py-3 hover:bg-[#1C2538] disabled:opacity-60"
                data-testid="invite-accept-btn"
              >
                {accepting ? t(lang,"common.loading") : t(lang,"team.join_btn")}
              </button>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
